/**
 * The model of the « Journal des avoirs fournisseur » (/avoirs, /avoirs/:id — design/ux/03 §2, §6.2;
 * refonte WP-30). Pure functions, unit tested: the page and its dialogs own no rule. The server stays
 * the authority — it filters, counts, sums, caps the règlement at the reste under the avoir lock and
 * refuses a cancellation the role may not do; this module only mirrors those rules so the screen can
 * explain a refusal before the request and never offers an action that would be refused.
 *
 *   · query      `paramsJournalAvoirs` / `paramsNotes` — the filter bar → the server's query
 *   · payload    `lireJournalAvoirs` / `lireNotes` — the server envelope (the AUD-AVOIR-02 contract)
 *   · chips      `optionsEtats` / `optionsRapprochement` — StatusChips options with the server counts
 *   · footer     `piedJournalAvoirs`
 *   · rights     `droitsAvoirs`, `actionsAvoir` — only the allowed actions are shown (AUD-AVOIR-26)
 *   · drawer     `phraseEtat`, `facturesTiroir`, `lignesTiroir`, `reglementsTiroir`
 *   · règlement  `varianteReglement`, `formReglementInitial`, `erreursReglement`, `payloadReglement`,
 *                `consequenceReglement`, `modesPourCompte`
 *   · annulation `phrasesAnnulation`, `motifAnnulationValide`
 *   · notes      `lienAvoirDepuisNote`, `optionsSuggestions`
 *   · print      `descriptionFiltres`
 *   · lifecycle  (refonte WP-83, D-32) `etatAffiche`, `cycleTiroir`, `liensCycle`, `annulationReserveeAuProprietaire`,
 *                `raisonAnnulationRefusee`; « Confirmer l'avoir » `formConfirmationInitial`, `optionsNotesConfirmation`,
 *                `montantParDefautConfirmation`, `erreursConfirmation`, `payloadConfirmation`, `effetsConfirmation`,
 *                `nouvelleCleConfirmation`, `succesConfirmation`
 */
import {
  AVOIR_BORNES,
  AVOIR_JOURNAL,
  ETAT_AVOIR,
  LIBELLES_DECISION_STOCK_LIGNE_AVOIR,
  LIBELLES_STATUT_COMPTABLE_AVOIR,
  LIBELLES_MOTIF_RETOUR,
  LIBELLES_NATURE_LIGNE_AVOIR,
  LIBELLES_TYPE_AVOIR,
  LIBELLES_TYPE_NOTE_DE_CREDIT,
  NATURE_LIGNE_AVOIR,
  RAPPROCHEMENT_NOTE,
  REQUETE_ID_AVOIR,
  ROLES_ANNULATION_AVOIR_REGLE,
  STATUT_COMPTABLE_AVOIR,
  TYPE_AVOIR,
  TYPES_AVOIR_SOLDE,
} from "@pharmakon/shared/constants/avoirs.js";
import { versCentimes, versTexteDecimal } from "@pharmakon/shared/pricing";
import { can } from "@/config/permissions.config.js";
import { fmtDate, fmtMoney } from "@/config/locale.config.js";
import { paramsJournal } from "@/lib/achat-journal.js";
import { modeReglementMeta } from "@/config/fournisseur.constants.js";
import {
  ORDRE_ETATS_AVOIR,
  ORDRE_RAPPROCHEMENT,
  TYPE_AVOIR_POUR_NOTE,
} from "@/config/avoir-journal.config.js";
import { M_AJ, M_ANNULATION, M_CONFIRMATION, M_NOTES, M_REGLEMENT, M_TIROIR } from "@/config/avoir-journal.messages.js";

const MONTANT_RE = /^\d{1,10}([.,]\d{1,2})?$/;
const c = (v) => (v == null || v === "" ? 0 : versCentimes(v));
const da = (v) => fmtMoney(v, { noSuffix: true });
const daC = (centimes) => da(versTexteDecimal(centimes));
const nomUtilisateur = (u) => (u ? [u.prenom, u.nom].filter(Boolean).join(" ") || null : null);

// ─── Query ───────────────────────────────────────────────────────────────────────────────────────

/**
 * The `GET /avoirs` (and `/avoirs/csv`) query: the filter bar's values (lists comma-joined, empty
 * values dropped), the selected états, the sort and the page. `etat` narrows the rows and the
 * footer only — the chip counts stay those of every état (server rule).
 * @param {Record<string, unknown>} queryParams  `useFilters().queryParams`
 * @param {{ etats?: string[], sort?: { by: string, order: string } | null, page?: number, limit?: number }} o
 */
export function paramsJournalAvoirs(queryParams, { etats = [], sort = null, page, limit } = {}) {
  const out = paramsJournal(queryParams, { onglet: undefined, sort, page, limit });
  delete out.onglet;
  if (page == null) delete out.page;
  if (limit == null) delete out.limit;
  const valides = etats.filter((e) => ORDRE_ETATS_AVOIR.includes(e));
  if (valides.length) out.etat = valides.join(",");
  return out;
}

/** The « Bon de retour lié » toggle of the journal (WP-31, `GET /avoirs?avec_bon_retour=true`). */
export const FILTRE_BON_RETOUR = "avec_bon_retour";

/** The journal filters a supplier's credit note has no field for (the notes tab hides and drops them). */
export const FILTRES_HORS_NOTES = Object.freeze(["type_avoir", FILTRE_BON_RETOUR]);

/**
 * The `GET /avoirs/notes-fournisseurs` query: the same bar (fournisseur, période, recherche — the
 * avoir type does not apply to a supplier's credit note) and the matching chips (one chip → that
 * state; none or both → every note).
 */
export function paramsNotes(queryParams, { rapprochements = [], page, limit } = {}) {
  const reste = Object.fromEntries(Object.entries(queryParams ?? {}).filter(([k]) => !FILTRES_HORS_NOTES.includes(k)));
  const out = paramsJournalAvoirs(reste, { page, limit });
  const r = rapprochementChoisi(rapprochements);
  if (r) out.rapprochement = r;
  return out;
}

/** One matching state when exactly one chip is pressed, else none (both = every note). */
export function rapprochementChoisi(selection) {
  const valides = (selection ?? []).filter((v) => ORDRE_RAPPROCHEMENT.includes(v));
  return valides.length === 1 ? valides[0] : null;
}

/** A comma list from the URL → its valid values, in the canonical order. */
export function lireListeUrl(texte, ordre) {
  const vus = new Set(String(texte ?? "").split(",").map((s) => s.trim()).filter(Boolean));
  return ordre.filter((v) => vus.has(v));
}

// ─── Payload contract (AUD-AVOIR-02) ─────────────────────────────────────────────────────────────

/**
 * The journal page from the server envelope. The API client resolves with the BODY
 * (`{ success, data, total, page, limit, compteurs, totaux }` — `controllers/avoir.controller.js`
 * spreads the service result), so the rows are `body.data`, never `body.data.data` (the former store
 * read `r.data` and destructured the rows array: the journal was always empty).
 * @param {any} body
 */
export function lireJournalAvoirs(body) {
  const avoirs = Array.isArray(body?.data) ? body.data : [];
  return {
    avoirs,
    total: Number.isInteger(body?.total) ? body.total : avoirs.length,
    page: Number.isInteger(body?.page) ? body.page : 1,
    limit: Number.isInteger(body?.limit) ? body.limit : AVOIR_JOURNAL.limit,
    compteurs: body?.compteurs ?? null,
    totaux: body?.totaux ?? null,
  };
}

/** The credit notes page from its envelope (`{ data, total, page, limit, compteurs }`). */
export function lireNotes(body) {
  const notes = Array.isArray(body?.data) ? body.data : [];
  return {
    notes,
    total: Number.isInteger(body?.total) ? body.total : notes.length,
    page: Number.isInteger(body?.page) ? body.page : 1,
    compteurs: body?.compteurs ?? null,
  };
}

// ─── Chips and footer ────────────────────────────────────────────────────────────────────────────

/**
 * The état chips (StatusChips options) with the server counts. No count while the counts are not a
 * trustworthy claim (loading, error).
 * @param {Record<string, number>|null} compteurs  `{ TOUS, EN_ATTENTE, … }`
 * @param {boolean} fiables
 */
export function optionsEtats(compteurs, fiables) {
  const n = (k) => (fiables && compteurs ? compteurs[k] ?? 0 : null);
  return {
    allLabel: M_AJ.etats.tous,
    allCount: n("TOUS"),
    options: ORDRE_ETATS_AVOIR.map((e) => ({
      value: e,
      label: M_AJ.etats[e].long,
      shortLabel: M_AJ.etats[e].court,
      status: `avoir_etat.${e}`,
      count: n(e),
    })),
  };
}

/** The matching chips of the credit notes tab. */
export function optionsRapprochement(compteurs, fiables) {
  const n = (k) => (fiables && compteurs ? compteurs[k] ?? 0 : null);
  return {
    allLabel: M_AJ.etats.tous,
    allCount: n("TOUS"),
    options: ORDRE_RAPPROCHEMENT.map((r) => ({ value: r, label: M_NOTES.etats[r], status: `note_rapprochement.${r}`, count: n(r) })),
  };
}

/** The label of the credit notes tab: « … · 3 à rapprocher » when some are. */
export function libelleOngletNotes(compteurs) {
  const n = compteurs?.[RAPPROCHEMENT_NOTE.A_RAPPROCHER] ?? 0;
  return n > 0 ? `${M_AJ.onglets.notes} · ${M_AJ.onglets.aRapprocher(n)}` : M_AJ.onglets.notes;
}

/**
 * ux/03 §2.1 — « 46 avoirs · HT … · TVA … · TTC … · Réglé … · Reste à régler … DA », over the whole
 * filter (the server's sums of the live avoirs); the cancelled ones of the filter are counted apart.
 * @param {{ nombre: number, annules?: number, montant_ht: string, montant_tva: string, montant_ttc: string, regle: string, reste: string }|null} totaux
 * @param {{ compact?: boolean }} [o]
 */
export function piedJournalAvoirs(totaux, { compact = false } = {}) {
  if (!totaux) return "";
  const v = {
    nombre: totaux.nombre ?? 0,
    ht: da(totaux.montant_ht),
    tva: da(totaux.montant_tva),
    ttc: da(totaux.montant_ttc),
    regle: da(totaux.regle),
    reste: da(totaux.reste),
  };
  const phrase = compact ? M_AJ.piedCompact(v) : M_AJ.pied(v);
  return totaux.annules > 0 ? `${phrase} (${M_AJ.piedAnnules(totaux.annules)})` : phrase;
}

// ─── Row display ─────────────────────────────────────────────────────────────────────────────────

/** ux/03 §1.4 label forms: `long` (drawer, print title), `court` (filter), `compact` (grid). */
export const libelleTypeAvoir = (type, forme = "court") => LIBELLES_TYPE_AVOIR[type]?.[forme] ?? "—";

/** The libellé of a connected fournisseur's credit note type (Avoir crédit · Remboursement · Remplacement). */
export const libelleTypeNote = (type) => LIBELLES_TYPE_NOTE_DE_CREDIT[type] ?? "—";


// ─── Rights and actions (AUD-AVOIR-26: an action is shown only when it would be accepted) ────────

/** The avoir rights of the signed-in user (UI gating; the server enforces). */
export function droitsAvoirs(user) {
  return {
    lire: can(user, "achat", "read"),
    creer: can(user, "achat", "update"),
    regler: can(user, "avoir", "mark_paid"),
    // refonte WP-83 (D-32, A-104 (4)) — « Confirmer l'avoir » (server `avoir:confirmer`: P, M)
    confirmer: can(user, "avoir", "confirmer"),
    annuler: can(user, "avoir", "annuler"),
    annulerRegle: Boolean(user?.role) && (user.role === "ADMIN_PHARMAKON" || ROLES_ANNULATION_AVOIR_REGLE.includes(user.role)),
    envoyer: can(user, "achat", "update"),
  };
}

/** Something already received (OP-03-10: « réglé » = statut ≠ en attente or an amount received). */
export const dejaRegle = (a) => a?.etat === ETAT_AVOIR.REGLE || a?.etat === ETAT_AVOIR.REGLE_PARTIELLEMENT || c(a?.regle) > 0;

/**
 * Refonte WP-83 (D-32, A-104 (8)) — the cancellations the owner alone may do: an avoir already (partly)
 * received, or an Écart / Produits avoir whose CONFIRMED credit is in the solde (`reduit_solde`, the
 * server's flag); an avoir en instance, outside the solde, is cancelled by P / M (`avoir:annuler`).
 */
export const annulationReserveeAuProprietaire = (a) => dejaRegle(a) || a?.reduit_solde === true;

/**
 * Refonte WP-83 × WP-90 (D-35, A-116; CP §6.2) — an avoir produits already settled in part by quantity
 * (a line's `quantite_resolue` while it is still en instance — the server's `aDesResolutions`): its
 * resolutions (lots delivered, credits) are the document's history, so it is neither cancelled nor
 * confirmed whole any more (409 AVOIR_REGLE_PAR_QUANTITE; `confirmable` false) — its remaining
 * quantities are settled with « Régler l'avoir produits » (WP-92).
 */
export const regleParQuantite = (a) =>
  a?.type_avoir === TYPE_AVOIR.PRODUITS &&
  a?.statut_comptable === STATUT_COMPTABLE_AVOIR.INSTANCE &&
  (a.details ?? []).some((d) => (d.quantite_resolue ?? 0) > 0);

/** The words of the server's refusal, said before the request (the grid's Suppr). */
export const raisonAnnulationRefusee = (a) =>
  regleParQuantite(a) ? M_ANNULATION.refuseRegleParQuantite(a.numero_avoir ?? "") : dejaRegle(a) ? M_ANNULATION.refuseRegle : M_ANNULATION.refuseConfirme;

/**
 * The drawer's actions for one avoir and the user's rights. Refonte WP-83 (D-32): an Écart / Produits
 * avoir en instance (`confirmable`, the server's rule) is « Confirmer l'avoir » — never « Enregistrer
 * le règlement », whose words say the solde does not move; nothing left to receive (a « Reçu non
 * facturé » avoir without credit, A-114 (6)) offers no règlement; an avoir produits settled in part by
 * quantity (WP-90) offers neither règlement nor cancellation.
 * @returns {{ confirmer: boolean, regler: boolean, annuler: boolean, imprimer: boolean, envoyer: boolean }}
 */
export function actionsAvoir(avoir, droits) {
  if (!avoir) return { confirmer: false, regler: false, annuler: false, imprimer: false, envoyer: false };
  const annule = avoir.etat === ETAT_AVOIR.ANNULE;
  const ouvert = avoir.etat === ETAT_AVOIR.EN_ATTENTE || avoir.etat === ETAT_AVOIR.REGLE_PARTIELLEMENT;
  const enInstance = avoir.confirmable === true;
  const aRecevoir = sansMontant(avoir) || c(avoir.reste) > 0;
  // WP-90 — settled in part by quantity: no règlement (its « Réglé » alias is the whole confirmation), no cancellation
  const parQuantite = regleParQuantite(avoir);
  return {
    confirmer: !annule && enInstance && Boolean(droits?.confirmer),
    regler: !annule && ouvert && !enInstance && !parQuantite && aRecevoir && Boolean(droits?.regler),
    annuler: !annule && !parQuantite && Boolean(droits?.annuler) && (!annulationReserveeAuProprietaire(avoir) || Boolean(droits?.annulerRegle)),
    imprimer: Boolean(droits?.lire),
    envoyer: !annule && Boolean(droits?.envoyer),
  };
}

// ─── Drawer ──────────────────────────────────────────────────────────────────────────────────────

const estRistourne = (a) => a?.type_avoir === TYPE_AVOIR.FINANCIER;

/** ux/03 §2.4 — the meaning of the état shown in the drawer. */
export function phraseEtat(avoir) {
  if (!avoir) return "";
  const S = M_TIROIR.sens;
  switch (avoir.etat) {
    case ETAT_AVOIR.ANNULE:
      return S.annule({ date: fmtDate(avoir.annule_at), par: avoir.annule_par_nom ?? nomUtilisateur(avoir.annule_par), motif: avoir.annulation_motif });
    case ETAT_AVOIR.REGLE_PARTIELLEMENT:
      return S.partiel({ regle: da(avoir.regle), reste: da(avoir.reste) });
    case ETAT_AVOIR.REGLE: {
      if (!estRistourne(avoir)) return S.regleFournisseur(avoir.reference_fournisseur);
      const dernier = [...(avoir.reglements ?? [])].reverse().find((r) => !r.annule);
      return S.regleRistourne({ date: fmtDate(dernier?.date ?? avoir.paid_at), mode: dernier?.mode ? libelleMode(dernier.mode) : null });
    }
    default:
      if (estRistourne(avoir)) return S[ETAT_AVOIR.EN_ATTENTE].ristourne;
      // refonte WP-83 (D-32) — en instance, the credit is not in the solde yet
      return avoir.statut_comptable === STATUT_COMPTABLE_AVOIR.INSTANCE ? S.instance(da(avoir.montant_ttc)) : S[ETAT_AVOIR.EN_ATTENTE].fournisseur;
  }
}

// ─── Lifecycle (refonte WP-83 — D-32, A-79, A-80, A-104; CP §3.2, §8.4, §9) ───────────────────────

/** The états a row and the drawer show (the registry `avoir_etat.*`). */
export const ETAT_AFFICHE_AVOIR = Object.freeze({
  INSTANCE: STATUT_COMPTABLE_AVOIR.INSTANCE,
  CONFIRME: STATUT_COMPTABLE_AVOIR.CONFIRME,
  REGLE_PARTIELLEMENT: ETAT_AVOIR.REGLE_PARTIELLEMENT,
  REGLE: ETAT_AVOIR.REGLE,
  ANNULE: ETAT_AVOIR.ANNULE,
});

/**
 * The état shown for an avoir: before anything is received (the server's « En attente »), its
 * lifecycle — « En instance » (outside the solde) or « Confirmé » (in it; a ristourne not yet
 * encaissée is recorded confirmed, A-101 (3)); then the règlement's état, or Annulé. Read from the
 * two server fields (`etat`, `statut_comptable`), never from a guess.
 */
export function etatAffiche(a) {
  if (!a) return null;
  if (a.etat !== ETAT_AVOIR.EN_ATTENTE) return a.etat;
  return a.statut_comptable === STATUT_COMPTABLE_AVOIR.INSTANCE ? ETAT_AFFICHE_AVOIR.INSTANCE : ETAT_AFFICHE_AVOIR.CONFIRME;
}

/**
 * The drawer's « Statut comptable » line (CP §9: recorded vs pending credits): en instance — outside the
 * solde; confirmé — when and by whom (the CONFIRME event of the avoir's trail), or at its recording
 * (confirmed at creation, the M-20 back-fill); a credit-less « Reçu non facturé » avoir; null for a
 * ristourne (revenue, never in the solde) and a cancelled avoir.
 */
export function cycleTiroir(avoir) {
  if (!avoir || estRistourne(avoir) || avoir.etat === ETAT_AVOIR.ANNULE) return null;
  const T = M_TIROIR.cycle;
  if (avoir.statut_comptable === STATUT_COMPTABLE_AVOIR.INSTANCE) return { ton: "warning", texte: T.instance };
  if (c(avoir.montant_ttc) === 0 && (avoir.recus_non_factures ?? 0) > 0) return { ton: "neutral", texte: T.sansCredit };
  const evt = [...(avoir.historique ?? [])].reverse().find((h) => h.evenement === EVENEMENT_CONFIRME);
  if (evt) return { ton: "info", texte: T.confirme({ date: fmtDate(evt.date), par: evt.par ?? null }) };
  return { ton: "info", texte: T.confirmeEnregistrement(avoir.confirme_at ? fmtDate(avoir.confirme_at) : null) };
}
/** The audit event a confirmation writes (server `EVENEMENT_AUDIT_AVOIR.CONFIRME`). */
const EVENEMENT_CONFIRME = "AVOIR_CONFIRME";

/**
 * The drawer's linked avoirs (A-80, A-104 (9)): the avoir this one comes from (a partial
 * confirmation's remainder, or a credit settling an avoir produits) and those derived from it.
 */
export function liensCycle(avoir) {
  const ligne = (a) => ({
    id: a.id,
    numero: a.numero_avoir,
    texte: M_TIROIR.lien({
      numero: a.numero_avoir,
      type: libelleTypeAvoir(a.type_avoir, "compact"),
      statut: a.annule_at ? M_TIROIR.lienAnnule : LIBELLES_STATUT_COMPTABLE_AVOIR[a.statut_comptable] ?? "—",
      ttc: da(a.montant_ttc),
    }),
  });
  return {
    origine: avoir?.avoir_origine ? ligne(avoir.avoir_origine) : null,
    derives: (avoir?.avoirs_derives ?? []).map(ligne),
  };
}

// ─── « Confirmer l'avoir » (refonte WP-83 — D-32, A-80, A-104 (4)-(6)) ─────────────────────────────

/** The creation-key bounds the server accepts (`REQUETE_ID_AVOIR`) — one key per opening of the dialog. */
export function nouvelleCleConfirmation(uuid = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`) {
  return `avc-${String(uuid).replace(/[^A-Za-z0-9_-]/g, "")}`.slice(0, REQUETE_ID_AVOIR.longueur_max);
}

/** The amount a confirmation states by default: the TTC; a note chosen → min(TTC, its TTC) (the server's default). */
export function montantParDefautConfirmation(avoir, note = null) {
  const ttc = c(avoir?.montant_ttc);
  return versTexteDecimal(note ? Math.min(ttc, c(note.montant_ttc)) : ttc);
}

/** The initial form: the reference already on the avoir, today (local), the TTC, no note. */
export function formConfirmationInitial(avoir, { aujourdhui }) {
  return {
    note: "",
    reference: avoir?.reference_fournisseur ?? "",
    date: aujourdhui,
    montant: montantParDefautConfirmation(avoir),
    observations: "",
  };
}

/** The « Avoir du fournisseur (plateforme) » choices: the fournisseur's credit notes still to match. */
export function optionsNotesConfirmation(notes) {
  return [
    { value: "", label: M_CONFIRMATION.champs.noteAucune },
    ...(notes ?? []).map((n) => ({
      value: n.id,
      label: M_CONFIRMATION.optionNote({ numero: n.numero, date: fmtDate(n.date_avoir), type: libelleTypeNote(n.type), ttc: da(n.montant_ttc) }),
    })),
  ];
}

const produits = (a) => a?.type_avoir === TYPE_AVOIR.PRODUITS;

/**
 * The refusals of the confirmation, field by field (empty = valid) — the server's rules said before
 * the request (A-104 (4)-(6)): the fournisseur's reference (typed, the chosen note's number, or already
 * on the avoir); a date; an amount > 0 and ≤ TTC; an avoir produits confirmed whole (a note of a lower
 * TTC cannot confirm it).
 */
export function erreursConfirmation(form, avoir, { note = null } = {}) {
  const E = M_CONFIRMATION.erreurs;
  const out = {};
  const ttc = c(avoir?.montant_ttc);
  if (!note) {
    const ref = String(form.reference ?? "").trim();
    if (!ref) out.reference = E.reference;
    else if (ref.length > AVOIR_BORNES.reference_max) out.reference = E.referenceLongue;
  }
  if (!form.date) out.date = E.date;
  const montant = normaliserMontant(form.montant);
  if (form.montant === "" || form.montant == null) out.montant = E.zero;
  else if (montant == null) out.montant = E.montantInvalide;
  else if (c(montant) <= 0) out.montant = E.zero;
  else if (c(montant) > ttc) out.montant = E.depasse(daC(ttc));
  else if (produits(avoir) && c(montant) < ttc) {
    out.montant = note && c(note.montant_ttc) < ttc ? E.noteInsuffisante(note.numero, da(note.montant_ttc), daC(ttc)) : E.produitsPartiel(daC(ttc));
  }
  return out;
}

/** The `POST /avoirs/:id/confirmer` body (strict schema): the key, the date, the amount, the note or the reference. */
export function payloadConfirmation(form, avoir, { note = null, requeteId }) {
  return {
    requete_id: requeteId,
    date: form.date,
    montant: normaliserMontant(form.montant),
    ...(note ? { note_de_credit_id: note.id } : { reference_fournisseur: String(form.reference).trim() }),
    ...(form.observations?.trim() ? { notes_paiement: form.observations.trim() } : {}),
  };
}

/**
 * What the confirmation does, said before it (CP §3.2, §11): the solde before → after (lowered by the
 * confirmed amount, once) and the pending credits before → after; a partial Écart confirmation's
 * remainder (A-80: a new avoir en instance). The solde unavailable → the effect in words.
 * @param {{ solde?: string, credits_en_attente_hors_solde?: string }|null} solde `GET /fournisseurs/:id/stats`
 * @returns {{ lignes: string[], reliquat: number|null }}
 */
export function effetsConfirmation(form, avoir, solde) {
  const E = M_CONFIRMATION.effets;
  const ttc = c(avoir?.montant_ttc);
  const saisi = normaliserMontant(form?.montant);
  const montant = saisi != null ? Math.min(c(saisi), ttc) : ttc;
  const reliquat = !produits(avoir) && montant > 0 && montant < ttc ? ttc - montant : null;
  const lignes = [];
  if (solde && solde.solde != null) {
    const avant = c(solde.solde);
    const attente = Math.max(0, c(solde.credits_en_attente_hors_solde));
    lignes.push(E.soldeAvantApres(phraseSolde(avant), phraseSolde(avant - montant)));
    lignes.push(E.attente(daC(attente), daC(Math.max(0, attente - montant))));
  } else {
    lignes.push(E.sansSolde(daC(montant)));
  }
  if (reliquat != null) lignes.push(E.reliquat(daC(reliquat)));
  lignes.push(E.uneFois);
  return { lignes, reliquat };
}

/** « 68 847,04 DA à payer » / « 1 200,00 DA en votre faveur » / « à jour » — the solde in words (55+). */
function phraseSolde(centimes) {
  if (centimes > 0) return M_CONFIRMATION.effets.aPayer(daC(centimes));
  if (centimes < 0) return M_CONFIRMATION.effets.enFaveur(daC(-centimes));
  return M_CONFIRMATION.effets.aJour;
}

/** The success sentence: the amount now in the solde, and the remainder's number after a partial confirmation. */
export function succesConfirmation(avoir) {
  const phrase = M_CONFIRMATION.succes(avoir?.numero_avoir ?? "", da(avoir?.regle ?? avoir?.montant_ttc ?? "0"));
  const r = avoir?.reliquat;
  return r ? `${phrase} ${M_CONFIRMATION.succesReliquat(r.numero_avoir ?? "", da(r.montant_ttc))}` : phrase;
}

/** The words of a `ModeReglement` (the one SPA table of the global enum, `modeReglementMeta`). */
export const libelleMode = (cle) => modeReglementMeta(cle).label;

/**
 * « F18/110837 (n° 112) · 21/08/2026 — 197,00 » rows of the factures imputées. Refonte WP-81 (CP §8.2,
 * A-81): a « Répartition par facture » shows the part typed for the facture (what its net dû took is in
 * the solde readers, the rest is the fournisseur-level credit), else what the avoir credits on it; a
 * facture only named (« Montant global » over several factures, a ristourne) reads « référence » —
 * never an invented share of the amount (the same words as the printed avoir).
 */
export function facturesTiroir(avoir) {
  return (avoir?.factures ?? []).map((f) => {
    const part = c(f.montant_ttc_reparti) > 0 ? f.montant_ttc_reparti : c(f.montant_ttc_impute) > 0 ? f.montant_ttc_impute : null;
    return {
      id: f.facture_achat_id,
      libelle: M_TIROIR.facture(f.numero_facture_fournisseur ?? "—", f.numero_interne, f.date_facture ? fmtDate(f.date_facture) : null),
      montant: part != null ? da(part) : null,
      reference: part == null && f.est_reference === true,
    };
  });
}

/**
 * The lines of the drawer: nature (and motif), product, quantity, TTC and the stock sentence.
 * Refonte WP-83 (EXE-88; A-106 (9), A-111, A-114): the motif is the one the line was written with
 * (`motif_libelle`, « label as written »), the D-11 return motif only for a legacy line without one;
 * the stock sentence is the line's persisted decision (`decision_stock`, WP-84) with the units it
 * moved, the bon de retour that carried the exit, the réclamation that already deducted them; a
 * « Reçu non facturé » line says its answer, its entry and its estimated value — it carries no credit.
 */
export function lignesTiroir(avoir) {
  return (avoir?.details ?? []).map((d) => {
    const retour = d.nature === NATURE_LIGNE_AVOIR.RETOUR;
    const rnf = d.recu_non_facture != null;
    const lot = (rnf ? d.lot_entree?.numero_lot ?? d.numero_lot : d.stock_lot?.numero_lot) ?? null;
    const bon = d.ligne_bon_retour?.bon_retour ?? null;
    const decision = d.decision_stock ? LIBELLES_DECISION_STOCK_LIGNE_AVOIR[d.decision_stock] ?? d.decision_stock_libelle ?? null : null;
    const details = [];
    if (bon) details.push(M_TIROIR.ligneRetourBon(lot, d.quantite_mouvementee || d.quantite, bon.numero ?? "—"));
    else if (decision) {
      const bouge = !rnf && (d.quantite_mouvementee ?? 0) > 0 ? M_TIROIR.ligneUnitesSorties(d.quantite_mouvementee) : null;
      details.push([lot ? M_TIROIR.lot(lot) : null, decision, bouge].filter(Boolean).join(" · "));
    } else details.push(retour ? M_TIROIR.ligneRetour(lot, d.quantite) : M_TIROIR.ligneManquant(lot));
    if (d.reclamation?.numero) details.push(M_TIROIR.ligneReclamation(d.reclamation.numero));
    if (rnf && d.valeur_estimee_ttc != null) details.push(M_TIROIR.ligneValeurEstimee(da(d.valeur_estimee_ttc)));
    if (!retour && !rnf && d.quantite_calculee != null && d.quantite_calculee !== d.quantite) details.push(M_TIROIR.ligneModifiee(d.quantite_calculee));
    const motifEcrit = d.motif_libelle?.trim() || null;
    return {
      id: d.id,
      nature: rnf ? motifEcrit ?? M_TIROIR.natureRecuNonFacture : LIBELLES_NATURE_LIGNE_AVOIR[d.nature] ?? "—",
      motif: rnf ? d.recu_non_facture_libelle ?? null : motifEcrit ?? (d.motif_retour ? LIBELLES_MOTIF_RETOUR[d.motif_retour] : null),
      produit: [d.produit?.nom_commercial, d.produit?.dosage].filter(Boolean).join(" "),
      quantite: d.quantite,
      montant: rnf ? "—" : da(c(d.montant_ttc) > 0 ? d.montant_ttc : d.montant_ht ?? "0"),
      detail: details.join(" · "),
    };
  });
}

/** The règlements of the drawer (W16 refunds for a ristourne, the received events otherwise). */
export function reglementsTiroir(avoir) {
  const ristourne = estRistourne(avoir);
  return (avoir?.reglements ?? []).map((r) => ({
    id: r.id,
    annule: Boolean(r.annule),
    texte: ristourne
      ? M_TIROIR.reglementRistourne({ date: fmtDate(r.date), montant: da(r.montant), mode: r.mode ? libelleMode(r.mode) : null, compte: r.compte, par: r.par })
      : M_TIROIR.reglementFournisseur({ date: fmtDate(r.date), montant: da(r.montant), reference: r.reference, par: r.par }),
  }));
}

// ─── Règlement (ux/03 §2.6, OP-03-08) ────────────────────────────────────────────────────────────

export const VARIANTE_REGLEMENT = Object.freeze({ FOURNISSEUR: "FOURNISSEUR", RISTOURNE: "RISTOURNE" });

/** Écart / Produits → « Avoir du fournisseur reçu »; ristourne → « Encaissement de la ristourne ». */
export const varianteReglement = (avoir) => (estRistourne(avoir) ? VARIANTE_REGLEMENT.RISTOURNE : VARIANTE_REGLEMENT.FOURNISSEUR);

/** A ristourne stored at 0,00 DA before M-10 (AUD-AVOIR-04): its first amount received is its amount (A-54 (8)). */
export const sansMontant = (avoir) => estRistourne(avoir) && c(avoir?.montant_ttc) === 0;

/** The cap of a règlement: the reste (none for a ristourne without amount). */
export const plafondCentimes = (avoir) => (sansMontant(avoir) ? null : Math.max(0, c(avoir?.reste)));

/** A typed amount → a normalised decimal string (« 1 234,5 » → « 1234.50 »), or null. */
export function normaliserMontant(texte) {
  if (texte == null) return null;
  const t = String(texte).replace(/[\s\u00a0\u202f]/g, "");
  if (!MONTANT_RE.test(t)) return null;
  return versTexteDecimal(versCentimes(t.replace(",", ".")));
}

/** The modes a receiving account accepts (the référentiel's `comptes[].modes`, else every mode). */
export function modesPourCompte(compte, modes) {
  const liste = modes ?? [];
  if (!compte?.modes?.length) return liste;
  return liste.filter((m) => compte.modes.includes(m.cle));
}

/**
 * The initial form. Amount = the reste (a note matched: min(reste, its TTC)); date = today (local,
 * AUD-AVOIR-23); a ristourne: the default receiving account and its first mode.
 * @param {object} avoir
 * @param {{ aujourdhui: string, note?: object|null, comptes?: object[], modes?: object[] }} o
 */
export function formReglementInitial(avoir, { aujourdhui, note = null, comptes = [], modes = [] }) {
  const plafond = plafondCentimes(avoir);
  const noteTtc = note ? c(note.montant_ttc) : null;
  let montant = plafond;
  if (noteTtc != null) montant = plafond == null ? noteTtc : Math.min(plafond, noteTtc);
  const compte = comptes.find((x) => x.is_defaut) ?? comptes[0] ?? null;
  const mode = modesPourCompte(compte, modes)[0]?.cle ?? "";
  return {
    montant: montant != null && montant > 0 ? versTexteDecimal(montant) : "",
    date: aujourdhui,
    reference: note?.numero ?? avoir?.reference_fournisseur ?? "",
    compte: compte?.id ?? "",
    mode,
    referencePaiement: "",
    observations: "",
  };
}

/**
 * The refusals of §2.6, field by field (empty object = valid). The server re-checks every one.
 * @param {object} form
 * @param {object} avoir
 * @param {{ note?: object|null, comptes?: object[], modes?: object[] }} [o]
 * @returns {Record<string, string>}
 */
export function erreursReglement(form, avoir, { note = null, comptes = [], modes = [] } = {}) {
  const E = M_REGLEMENT.erreurs;
  const out = {};
  const montant = normaliserMontant(form.montant);
  const plafond = plafondCentimes(avoir);
  if (form.montant === "" || form.montant == null) out.montant = E.zero;
  else if (montant == null) out.montant = E.montantInvalide;
  else if (c(montant) <= 0) out.montant = E.zero;
  else if (plafond != null && c(montant) > plafond) out.montant = E.depasse(daC(plafond));
  if (!form.date) out.date = E.date;
  if (varianteReglement(avoir) === VARIANTE_REGLEMENT.RISTOURNE) {
    const compte = comptes.find((x) => x.id === form.compte);
    if (comptes.length === 0) out.compte = E.aucunCompte;
    else if (!compte) out.compte = E.compte;
    const mode = modesPourCompte(compte, modes).find((m) => m.cle === form.mode);
    if (!mode) out.mode = E.mode;
    else if (mode.reference_requise && !String(form.referencePaiement ?? "").trim()) out.referencePaiement = E.referencePaiement;
  } else if (!note) {
    const ref = String(form.reference ?? "").trim();
    if (!ref) out.reference = E.reference;
    else if (ref.length > AVOIR_BORNES.reference_max) out.reference = E.referenceLongue;
  }
  return out;
}

/**
 * The `POST /avoirs/:id/regler` body. A note matched: `note_de_credit_id` (its number becomes the
 * reference, server-side). The ristourne key is forged at SEND time (one send is idempotent, a
 * second deliberate règlement gets its own key).
 */
export function payloadReglement(form, avoir, { note = null, idempotencyKey = null } = {}) {
  const corps = {
    montant: normaliserMontant(form.montant),
    date: form.date,
    ...(form.observations?.trim() ? { notes_paiement: form.observations.trim() } : {}),
  };
  if (note) corps.note_de_credit_id = note.id;
  if (varianteReglement(avoir) === VARIANTE_REGLEMENT.RISTOURNE) {
    corps.destination_emplacement_id = form.compte;
    corps.mode = form.mode;
    if (form.referencePaiement?.trim()) corps.reference = form.referencePaiement.trim();
    if (idempotencyKey) corps.idempotency_key = idempotencyKey;
  } else if (!note) {
    corps.reference_fournisseur = String(form.reference).trim();
  }
  return corps;
}

/** The consequence line under the fields (§2.6), for the typed amount. */
export function consequenceReglement(form, avoir) {
  const montant = normaliserMontant(form.montant);
  if (varianteReglement(avoir) === VARIANTE_REGLEMENT.RISTOURNE) return M_REGLEMENT.consequenceRistourne(da(montant ?? "0"));
  const plafond = plafondCentimes(avoir);
  if (montant != null && plafond != null && c(montant) > 0 && c(montant) < plafond) {
    return M_REGLEMENT.consequencePartielle(daC(plafond - c(montant)));
  }
  return M_REGLEMENT.consequenceFournisseur;
}

// ─── Annulation (OP-03-10) ───────────────────────────────────────────────────────────────────────

/** The consequence sentences of the cancellation dialog (what the server will do, A-54 (6) / (9)). */
export function phrasesAnnulation(avoir) {
  if (!avoir) return [];
  const out = [M_ANNULATION.conserve];
  // refonte WP-83 (D-32) — only a CONFIRMED credit is in the solde; an instance one only leaves the pending credits
  if (TYPES_AVOIR_SOLDE.includes(avoir.type_avoir) && c(avoir.montant_ttc) > 0) {
    if (avoir.statut_comptable === STATUT_COMPTABLE_AVOIR.INSTANCE) out.push(M_ANNULATION.soldeInstance(da(avoir.montant_ttc)));
    else out.push(M_ANNULATION.solde(da(avoir.montant_ttc)));
  }
  if (estRistourne(avoir)) out.push(c(avoir.regle) > 0 ? M_ANNULATION.ristourne(da(avoir.regle)) : M_ANNULATION.ristourneSansEncaissement);
  const retours = (avoir.details ?? []).filter((d) => d.nature === NATURE_LIGNE_AVOIR.RETOUR);
  // WP-31 — units credited through a bon de retour stay out (OP-03-10); a legacy line's come back
  const viaBon = retours.filter((d) => d.ligne_bon_retour?.bon_retour);
  const legacy = retours.filter((d) => !d.ligne_bon_retour?.bon_retour);
  const unitesBon = viaBon.reduce((s, d) => s + (d.quantite ?? 0), 0);
  const unitesLegacy = legacy.reduce((s, d) => s + (d.quantite ?? 0), 0);
  if (unitesBon > 0) out.push(M_ANNULATION.retoursRestent(unitesBon, [...new Set(viaBon.map((d) => d.ligne_bon_retour.bon_retour.numero))].join(", ")));
  if (unitesLegacy > 0) out.push(M_ANNULATION.retours(unitesLegacy));
  return out;
}

/**
 * After « Annuler l'avoir » (ux/03 §8 E-AV-3, « cancelling the avoir leaves stock out and says so »):
 * the success sentence, with the bons de retour whose units stay out (`stock_reste_sorti`, WP-31).
 */
export function succesAnnulation(avoir) {
  const bons = (avoir?.stock_reste_sorti ?? []).map((b) => b.numero).filter(Boolean);
  const phrase = M_ANNULATION.succes(avoir?.numero_avoir ?? "");
  return bons.length ? `${phrase} ${M_ANNULATION.succesStock(bons.join(", "))}` : phrase;
}

export const motifAnnulationValide = (motif) => String(motif ?? "").trim().length >= AVOIR_BORNES.motif_min;

// ─── Connected fournisseurs' credit notes (§2.7) ─────────────────────────────────────────────────

/**
 * « Créer l'avoir correspondant » — the editor's deep link (ux/03 §1.1) pre-filled from the note:
 * its type (A-57), the facture imported from its e-facture, its TTC (Écart) and its number as the
 * fournisseur's reference.
 */
export function lienAvoirDepuisNote(note) {
  const type = TYPE_AVOIR_POUR_NOTE[note?.type] ?? TYPE_AVOIR.ECART;
  const p = new URLSearchParams();
  p.set("type", type);
  if (note?.e_facture_id) p.set("efacture", note.e_facture_id);
  if (type === TYPE_AVOIR.ECART && c(note?.montant_ttc) > 0) p.set("montant", versTexteDecimal(c(note.montant_ttc)));
  if (note?.numero) p.set("reference", note.numero);
  return `/avoirs/nouveau?${p.toString()}`;
}

/** The « Rapprocher… » choices: suggestions first (server order), with their reason. */
export function optionsSuggestions(suggestions) {
  return (suggestions ?? []).map((a) => ({
    value: a.id,
    label: M_NOTES.rapprocher.option({
      numero: a.numero_avoir,
      date: fmtDate(a.date_avoir),
      type: libelleTypeAvoir(a.type_avoir, "compact"),
      ttc: da(a.montant_ttc),
      reste: da(a.reste),
    }),
    hint: a.suggestion ? M_NOTES.rapprocher.suggestion[a.suggestion] : undefined,
  }));
}

// ─── Print (§6.2) ────────────────────────────────────────────────────────────────────────────────

/**
 * The « active filters » line of the printed journal.
 * @param {{ fournisseurs?: string[], types?: string[], etats?: string[], recherche?: string }} f
 */
export function descriptionFiltres({ fournisseurs = [], types = [], etats = [], recherche = "", bonRetour = false } = {}) {
  const parts = [];
  if (fournisseurs.length) parts.push(`${M_AJ.filtres.fournisseur} : ${fournisseurs.join(", ")}`);
  if (types.length) parts.push(`${M_AJ.filtres.type} : ${types.map((t) => libelleTypeAvoir(t)).join(", ")}`);
  if (etats.length) parts.push(`${M_AJ.filtres.etat} : ${etats.map((e) => M_AJ.etats[e]?.long ?? e).join(", ")}`);
  if (bonRetour) parts.push(M_AJ.filtres.bonRetour);
  if (recherche) parts.push(`« ${recherche} »`);
  return parts.join(" · ");
}
